import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      setMessage('No payment session found');
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/api/payments/status/${sessionId}`, { withCredentials: true });
        
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          setMessage(`Payment of $${res.data.amount.toFixed(2)} successful!`);
          return true;
        } else if (res.data.status === 'expired') {
          setStatus('error');
          setMessage('Payment session expired');
          return true;
        }
        return false;
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login');
          return true;
        }
        return false;
      }
    };

    const poll = async () => {
      const done = await checkStatus();
      if (!done && attempts < maxAttempts) {
        attempts++;
        setTimeout(poll, pollInterval);
      } else if (!done) {
        setStatus('error');
        setMessage('Could not verify payment. Please check your dashboard.');
      }
    };

    poll();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-vault flex items-center justify-center p-4">
      <div className="glass-modal p-8 rounded-lg max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="h-16 w-16 text-[#9333EA] animate-spin mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold text-white mb-2">
              Verifying Payment
            </h2>
            <p className="text-[#A19DB0]">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold text-white mb-2">
              Payment Successful!
            </h2>
            <p className="text-[#A19DB0] mb-6">{message}</p>
            <Button
              data-testid="return-to-dashboard-button"
              onClick={() => navigate('/dashboard')}
              className="btn-primary w-full"
            >
              Return to Dashboard
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-16 w-16 text-red-400 mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold text-white mb-2">
              Payment Issue
            </h2>
            <p className="text-[#A19DB0] mb-6">{message}</p>
            <Button
              data-testid="return-to-dashboard-button"
              onClick={() => navigate('/dashboard')}
              className="btn-primary w-full"
            >
              Return to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
